import buildAlgoliaRequests from './buildAlgoliaRequests';
import { generateAlgoliaFilters } from './filtersToQueryString';
import * as Sentry from '@sentry/node';

const getConfig = require('next/config').default;
const envConfig = getConfig().publicRuntimeConfig;
const algoliaAppId = envConfig.ALGOLIA_APP_ID;
const algoliaSearchApiKey = envConfig.ALGOLIA_SEARCH_API_KEY;

/**
 * @typedef {Object} AlgoliaSearchOptions
 * @property {Record<string, string[]>} [filters={}] - Filters to apply for the search. Keys are field names, values are arrays of possible values.
 * @property {string[]} [privateGroupIds=[]] - Array of private group IDs to include in the search.
 * @property {Object} pagination - Pagination settings for the search.
 * @property {number} [pagination.page=1] - The current page number (1-indexed).
 * @property {number} [pagination.hitsPerPage=20] - The number of results per page.
 * @property {string[] | null} [facets=null] - List of facets to retrieve, or `null` for the default set of facets.
 * @property {string[]} [courseVisibility=[]] - Array of course visibility.
 */

/**
 * Fetches search results from Algolia with the given parameters.
 *
 * @param {string} search - The search query string.
 * @param {AlgoliaSearchOptions} options - The options for configuring the search request.
 * @returns {Promise<{
 *   searchResult: {
 *     hits: Object[], // Array of search results.
 *     nbHits: number // Total number of hits for the query.
 *   },
 *   facets: Record<string, any>, // Facet counts, keyed by facet name.
 *   searchResponseStatus: number // HTTP response status of the Algolia request.
 * }>} - The search results, facets, and HTTP status.
 */
export default async function fetchSearch(search, options) {
  /** @type {AlgoliaSearchOptions}  */
  const defaultOptions = {
    filters: {},
    privateGroupIds: [],
    pagination: {
      page: 1,
      hitsPerPage: 20,
    },
    facets: null,
    courseVisibility: [],
  };
  options = {
    ...defaultOptions,
    ...options,
  };

  let algoliaContentIndex = envConfig.ALGOLIA_CONTENT_INDEX;
  // for events pages, if search string is empty, we sort the results by event start date
  if (
    search === '' &&
    Array.isArray(options.filters.types) &&
    options.filters.types.includes('event')
  ) {
    // 1 - when requesting only future events, we want the nearest events to appear first (so we use ASC-sorted replica)
    // 2 - when requesting all events, we want the latest events to appear first (so we use DESC-sorted replica)
    algoliaContentIndex +=
      Array.isArray(options.filters.onlyShowFutureEvents) &&
      options.filters.onlyShowFutureEvents.includes('true')
        ? '_events_start_date_asc'
        : '_events_start_date_desc';
  }

  const now = parseInt(Date.now() / 1000);
  const indexedFacetRequests = buildAlgoliaRequests(
    algoliaContentIndex,
    search,
    options.filters,
    options.privateGroupIds,
    options.courseVisibility,
    now
  );

  const facetString = options.facets
    ? options.facets.join(',')
    : 'userType,private,types,themes,disciplines,learnerAges,sequencings,activityTypes,CECRLevels,trainingGoals,mediaTypes,newsTypes,eventTypes,jobs,schoolType,subjects,hasAssociation,mainCategories,subCategories,publics,skills';

  let cacheKey = ``;
  if (typeof window !== 'undefined') {
    cacheKey = `s=${search}`;
    Object.entries(options.filters).forEach(entry => {
      cacheKey = `${cacheKey},${entry[0]}=${entry[1]}`;
    });
  }

  return await fetch(
    `https://${algoliaAppId}-dsn.algolia.net/1/indexes/*/queries?X-Algolia-Application-Id=${algoliaAppId}&X-Algolia-API-Key=${algoliaSearchApiKey}#${cacheKey}`,
    {
      method: 'POST',
      body: JSON.stringify({
        requests: [
          // search request
          {
            indexName: algoliaContentIndex,
            params:
              'query=' +
              search +
              '&facets=' +
              facetString +
              '&filters=' +
              generateAlgoliaFilters(
                options.filters,
                options.privateGroupIds,
                options.courseVisibility,
                now
              ) +
              '&attributesToRetrieve=' +
              'types,__TYPE__,id,' +
              'displayName,slug,title,abstract,author,group,themes,learnerAges,mediaTypes,sequencings,activityTypes,CECRLevels,trainingGoals,mediaTypes,newsTypes,eventTypes,start,end,allDay,countSelections,countRecommendations,countComments,diamond,publishedAtTimestamp,updatedAtTimestamp,labelized,subtitle,translatedMainCategories,translatedSubCategories,translatedPublics,translatedSkills,translatedCECRLevels,' +
              'jobs,disciplines,country,avatar,partnership,isExpert,' +
              'name,goal,target,subjects,admin,countMembers,countPublications,private,roles,' +
              'location,image,' +
              'courseSkills,courseExternalSource,duration,countSubscribedUsers,' +
              'startTimestamp,endTimestamp,timeZone,summary,' +
              'town' +
              '&hitsPerPage=' +
              options.pagination.hitsPerPage +
              '&page=' +
              (options.pagination.page - 1),
          },
          // facet requests
          ...indexedFacetRequests.map(facetRequest => facetRequest.request),
        ],
      }),
    }
  ).then(async res => {
    if (res.status >= 400) {
      const error = await res.json();
      Sentry.withScope(scope => {
        scope.setExtra('algolia', error);
        Sentry.captureException(new Error('Algolia fetch error'));
      });
      return {
        searchResult: {
          hits: [],
          nbHits: 0,
        },
        facets: options.facets,
        searchResponseStatus: res.status,
      };
    }
    return res.json().then(json => {
      // facet counts from search query
      let facets = json.results[0].facets;
      // replace facet counts for facets that have been separately requested
      indexedFacetRequests.map((facetRequest, index) => {
        facets[facetRequest.name] =
          json.results[index + 1].facets[facetRequest.name];
      });

      return {
        searchResult: json.results[0],
        facets,
        searchResponseStatus: res.status,
      };
    });
  });
}

export async function fetchLabelizedMainCategories() {
  return fetchSearch('', {
    filters: { labelized: [1] },
    pagination: {
      page: 1,
      hitsPerPage: 0,
    },
    facets: ['mainCategories'],
  });
}
